import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { login } from 'features/auth/api/login';
import { loginOtp } from 'features/auth/api/request-login-otp';
import { hasSQLInjection } from 'shared/lib/secure';
import usePostLoginSetup from './usePostLoginSetup';

const OTP_DURATION = 180;
const MAX_WRONG_ATTEMPTS = 3;

const useLoginOtp = ({ setLoginOtp, loginOtp: isLoginOtp, formData }) => {
  const { t } = useTranslation('common');
  const { executeSetup, isSettingUp } = usePostLoginSetup();
  const [errors, setErrors] = useState({});
  const [otpValue, setOtpValue] = useState('');
  const [timeLeft, setTimeLeft] = useState(OTP_DURATION);
  const [wrongAttempts, setWrongAttempts] = useState(0);

  useEffect(() => {
    if (!isLoginOtp || timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isLoginOtp, timeLeft]);

  useEffect(() => {
    if (isLoginOtp) {
      setTimeLeft(OTP_DURATION);
    }
  }, [isLoginOtp]);

  useEffect(() => {
    if (wrongAttempts === MAX_WRONG_ATTEMPTS) {
      toast.error(t('login_failed_max_attempts'));
    }
  }, [wrongAttempts, t]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const resendOtpMutation = useMutation((payload) => loginOtp(payload), {
    onSuccess: () => {
      setTimeLeft(OTP_DURATION);
      setOtpValue('');
      setLoginOtp(true);
    },
    onError: (error) => {
      const message =
        error?.response?.data?.errors?.[0]?.message ||
        error?.response?.data?.message ||
        error?.message ||
        t('server_error');
      setErrors({ error: message });
    },
  });

  const loginMutation = useMutation((payload) => login(payload), {
    onSuccess: (res) => {
      executeSetup(res);
    },
    onError: (error) => {
      setWrongAttempts((prev) => {
        const newCount = prev + 1;
        if (newCount >= MAX_WRONG_ATTEMPTS) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            maxAttempts: t('login_failed_max_attempts'),
          }));
          setLoginOtp(false);
        }
        return newCount;
      });
      setErrors((prevErrors) => ({
        ...prevErrors,
        error: error?.response?.data?.errors?.[0]?.message || error?.message,
      }));
    },
  });

  const handleResendOtp = () => {
    if (timeLeft !== 0) return;
    setErrors({});

    const email = formData.email?.trim() || '';
    const password = formData.password?.trim() || '';

    if (!email || !password) {
      setErrors({ error: t('login_invalid_data') });
      return;
    }

    resendOtpMutation.mutate({ email, password });
  };

  const handleLoginOtp = () => {
    if (loginMutation.isLoading || isSettingUp) return;

    const newErrors = {};

    if (otpValue === '') {
      newErrors.otpValue = t('otp_required');
    }

    if (hasSQLInjection(otpValue)) {
      newErrors.otpValue = t('otp_invalid');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    loginMutation.mutate({
      email: formData.email,
      password: formData.password,
      otp: otpValue,
    });
  };

  return {
    errors,
    setErrors,
    loadingOtp: loginMutation.isLoading || isSettingUp,
    otpValue,
    setOtpValue,
    timeLeft,
    formatTime,
    handleResendOtp,
    handleLoginOtp,
    wrongAttempts,
  };
};

export default useLoginOtp;
